import { useTranslation } from 'react-i18next';

import { AI_QUOTA_USAGE_STATUS, useAIQuota } from 'core/hooks/useAIQuota';

import { CONVERSATION_MODES, type ConversationMode } from '../../../constants/modes';
import { useAIChatConversation } from '../../../contexts/AIChatConversation/useAIChatConversation';
import { type QuotaFirstUseNotice, getQuotaFirstUseNotice } from '../../../utils/quotaFirstUseNotice';

type Params = {
  hasMessages: boolean;
  mode: ConversationMode;
};

/**
 * The notice lasts while the student has spent no interaction. In study it ignores earlier
 * conversations, kept from before the limit existed.
 */
function useChatQuotaFirstUseNotice({ hasMessages, mode }: Params): QuotaFirstUseNotice | null {
  const { t } = useTranslation(['chatbot']);
  const { usage } = useAIQuota();
  const conversation = useAIChatConversation({ optional: true });

  // A practice conversation only exists from its first message. «No lo sé» and a wrong answer queue
  // theirs before the panel opens, so a queued or sent message means the chat opened on it, not on
  // the notice. The automaticMessage flag cannot tell: «No lo sé» does not carry it.
  const hasQueuedMessage = (conversation?.messageQueue?.length ?? 0) > 0;
  const hasPracticeStarted = mode === CONVERSATION_MODES.PRACTICE && (hasMessages || hasQueuedMessage);

  if (usage?.status !== AI_QUOTA_USAGE_STATUS.UNUSED || hasPracticeStarted) {
    return null;
  }

  return getQuotaFirstUseNotice(t, usage.quota);
}

export default useChatQuotaFirstUseNotice;
